import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Trash2 } from "lucide-react";

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "executive" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success("Team member added");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", password: "", role: "executive" });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="p-5 md:p-10 max-w-[1200px] rise" data-testid="team-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-tiny">Access control</p>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-bold tracking-tighter">Team</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button" className="rounded-lg">
              <UserPlus size={16} className="mr-2" /> Add member
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display font-bold tracking-tight">Add team member</DialogTitle>
              <DialogDescription>Field executives can only see assets assigned to them.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="user-form">
              <div>
                <Label className="label-tiny">Full name</Label>
                <Input required className="mt-2" data-testid="user-name-input" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label className="label-tiny">Email</Label>
                <Input required type="email" className="mt-2" data-testid="user-email-input" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label className="label-tiny">Phone</Label>
                <Input className="mt-2" data-testid="user-phone-input" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <Label className="label-tiny">Password (min 6 chars)</Label>
                <Input required minLength={6} type="text" className="mt-2" data-testid="user-password-input"
                  value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
              <div>
                <Label className="label-tiny">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-2" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="executive">Field executive</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={busy} data-testid="user-submit-button" className="rounded-lg">
                  {busy ? "Saving…" : "Add member"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 surface divide-y divide-border" data-testid="team-list">
        {users.map((u) => (
          <div key={u.id} data-testid={`team-row-${u.id}`} className="flex items-center justify-between gap-4 p-5">
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate">{u.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                {u.email} {u.phone ? `· ${u.phone}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <span className="label-tiny">{u.role}</span>
              <span className="text-xs font-mono text-muted-foreground">{u.assigned_parcels} assets</span>
              {u.id !== user.id && (
                <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u.id)}
                  className="text-muted-foreground hover:text-destructive transition-colors duration-200">
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
